import { ReactNode, useEffect, useState, createContext, useContext } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { useAppConfig } from '../hooks/useAppConfig';
import { centsToFloat } from '../functions/price_functions';
import { useLocation } from 'react-router-dom';


type PluginFacebook = {
    able: boolean;
    debug: boolean;
    facebookPixelId: string;
}
type ProductTrackingData = {
    id: string | number;
    name: string;
    sku?: string;
    price: number;
    quantity: number;
    category?: string;
}

type FacebookPixelContextData = {
    pluginFacebook: PluginFacebook;
    trackPageView: () => void;
    trackAddToCart: (value: number, productData: ProductTrackingData) => void;
    trackInitiateCheckout: () => void;
    trackPurchase: (value: number) => void;
    trackRemoveFromCart: (value: number, productData: ProductTrackingData) => void;
    trackQuantityChange: (productValue: number, action: 'increase' | 'decrease', productData: ProductTrackingData) => void;
}

type FacebookPixelContextProviderProps = {
    children: ReactNode;
};

export const FacebookPixelContext = createContext({} as FacebookPixelContextData);

export function FacebookPixelContextProvider({ children }: FacebookPixelContextProviderProps) {
    const location = useLocation();
    const useApp = useAppConfig();

    const [pluginFacebook, setPluginFacebook] = useState<PluginFacebook>({
        able: false,
        facebookPixelId: "",
        debug: false
    });

    function getUTMParams() {
        const searchParams = new URLSearchParams(location.search);
        const utmParams = {
            utm_source: searchParams.get('utm_source'),
            utm_medium: searchParams.get('utm_medium'),
            utm_campaign: searchParams.get('utm_campaign'),
            utm_term: searchParams.get('utm_term'),
            utm_content: searchParams.get('utm_content'),
        };
        return utmParams;
    }


    async function getPluginFacebook() {
        try {
            const plugins = await useApp.getPluginconfig();
            const pluginFacebookConfig = plugins.find(
                (plugin) => [269, 268, 267].includes(plugin.plugintemplateId)
            );
            if (pluginFacebookConfig) {
                const content = pluginFacebookConfig.content;
                setPluginFacebook({
                    able: content.able.toString() === '1',
                    facebookPixelId: content.pixel || "",
                    debug: content?.debug && content?.debug.toString() === '1',
                });
            }
        } catch (error) {
            // handle error if needed
        }
    }

    async function initFacebookPixel() {
        try {
            if (pluginFacebook.able && pluginFacebook.facebookPixelId) {
                ReactPixel.init(pluginFacebook.facebookPixelId);
                // Dispara PageView inicial automaticamente após init
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                pluginFacebook.debug && console.log('Iniciando o Pixel do Facebook')
            }
        } catch (error) {
            console.log('Error tracking initFacebookPixel:', error);

        }
    }

    // Evento 1: Visualizações da tela inicial da loja
    function trackPageView() {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                pluginFacebook.debug && console.log('Traking o Page View')
            }
        } catch (error) {
            console.log('Error tracking trackPageView:', error);
        }
    }

    // Evento 2: Add produto ao carrinho
    function trackAddToCart(value: number, productData: ProductTrackingData) {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                ReactPixel.track('AddToCart', {
                    value: value,
                    currency: 'BRL',
                    content_name: productData.name,
                    content_ids: [productData.id.toString()],
                    content_type: 'product',
                    ...utmParams
                });
                pluginFacebook.debug && console.log('Adicionando produto ao carrinho:', {
                    produto: productData.name,
                    valor: value,
                    id: productData.id,
                    sku: productData.sku,
                    quantidade: productData.quantity
                });
            }
        } catch (error) {
            console.log('Error tracking trackAddToCart:', error);
        }
    }

    // Evento 3: Início do checkout
    function trackInitiateCheckout() {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                ReactPixel.track('InitiateCheckout');

                pluginFacebook.debug && console.log('Iniciando o checkout para pagamento')

            }
        } catch (error) {
            console.log('Error tracking InitiateCheckout:', error);
        }
    }

    // Evento 4: Evento de compra
    function trackPurchase(value: number) {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                ReactPixel.track('Purchase', {
                    value: centsToFloat(value),
                    currency: 'BRL'
                });
                pluginFacebook.debug && console.log('Realizando o pagamento do carrinho')

            }
        } catch (error) {
            console.log('Error tracking trackPurchase:', error);

        }
    }
    // Evento 5: Remover produto do carrinho
    function trackRemoveFromCart(value: number, productData: ProductTrackingData) {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();
                ReactPixel.track('PageView', {
                    ...utmParams,
                });
                ReactPixel.track('RemoveFromCart', {
                    value: value,
                    currency: 'BRL',
                    content_name: productData.name,
                    content_ids: [productData.id.toString()],
                    content_type: 'product',
                    ...utmParams
                });
                pluginFacebook.debug && console.log('Removendo produto do carrinho:', {
                    produto: productData.name,
                    valor: value,
                    id: productData.id,
                    quantidade: productData.quantity
                });
            }
        } catch (error) {
            console.log('Error tracking trackRemoveFromCart:', error);
        }
    }
    // Evento 6: Alteração de quantidade no carrinho
    function trackQuantityChange(productValue: number, action: 'increase' | 'decrease', productData: ProductTrackingData) {
        try {
            if (pluginFacebook.able) {
                const utmParams = getUTMParams();

                if (action === 'increase') {
                    ReactPixel.track('AddToCart', {
                        value: productValue,
                        currency: 'BRL',
                        content_name: productData.name,
                        content_ids: [productData.id.toString()],
                        content_type: 'product',
                        ...utmParams
                    });
                    pluginFacebook.debug && console.log('Aumentando quantidade do produto:', {
                        produto: productData.name,
                        valor: productValue,
                        ação: 'aumento',
                        id: productData.id,
                        quantidade: productData.quantity
                    });
                } else {
                    ReactPixel.track('RemoveFromCart', {
                        value: productValue,
                        currency: 'BRL',
                        content_name: productData.name,
                        content_ids: [productData.id.toString()],
                        content_type: 'product',
                        ...utmParams
                    });
                    pluginFacebook.debug && console.log('Diminuindo quantidade do produto:', {
                        produto: productData.name,
                        valor: productValue,
                        ação: 'diminuição',
                        id: productData.id,
                        quantidade: productData.quantity
                    });
                }
            }
        } catch (error) {
            console.log('Error tracking trackQuantityChange:', error);
        }
    }

    useEffect(() => {
        getPluginFacebook();
    }, []);

    useEffect(() => {
        if (pluginFacebook.able && pluginFacebook.facebookPixelId) {
            initFacebookPixel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pluginFacebook.able, pluginFacebook.facebookPixelId]);



    return (
        <FacebookPixelContext.Provider value={{
            pluginFacebook,
            trackPageView,
            trackAddToCart,
            trackInitiateCheckout,
            trackPurchase,
            trackRemoveFromCart,
            trackQuantityChange
        }}>
            {children}
        </FacebookPixelContext.Provider>
    );
}

export function useFacebookPixel() {
    const context = useContext(FacebookPixelContext);
    if (!context) {
        throw new Error('useFacebookPixel must be used within a FacebookPixelContextProvider');
    }
    return context;
}